import { cn } from '@sohanemon/utils';
import { m } from '@sohanscript/i18n';
import { BgCircle } from '@sohanscript/ui/bg-circle';
import { SeSvg } from '@sohanscript/ui/se-svg';
import type { ComponentProps } from 'react';
import { useSohanEmon } from '#/lib/hooks/sohanemon.tsx';
import { getBrand } from '#/lib/utils/app-env.ts';
import { TypeWriter } from '@/components/type-writer';
import type { LinkTo } from './link';
import {
	AnimatedSpan,
	Terminal,
	TerminalInfo,
	TerminalLink,
	TerminalSuccess,
	TypingAnimation,
} from './ui/terminal';

type HeroProps = ComponentProps<'div'> & { bare?: boolean };

export function MainHero({ className, bare = false, ...props }: HeroProps) {
	const {
		msg: contactMsg,
		href: contactHref,
		target,
		label: contactLabel,
	} = useSohanEmon().contact;

	const name = getBrand().domain;

	const terminal = (
		<Terminal bare={bare} className={bare ? undefined : 'max-h-auto max-w-2xl'}>
			<TypingAnimation>{`~ ${name} init --profile`}</TypingAnimation>
			<AnimatedSpan>
				<div className="relative mx-auto w-fit [--circle-size:192px] [--se-factor:60] lg:[--se-factor:80]">
					<SeSvg
						parallax={false}
						className="absolute inset-0 m-auto h-[calc(var(--circle-size,192px)*var(--se-factor)/192)] w-[calc(var(--circle-size,192px)*var(--se-factor)/192)] active:stroke-primary/60"
					/>
					<BgCircle parallax={false} />
				</div>
			</AnimatedSpan>
			<TerminalSuccess>{m.hero_profile_loaded()}</TerminalSuccess>
			<TerminalSuccess>{m.hero_stack()}</TerminalSuccess>
			<TerminalInfo>{m.hero_available()}</TerminalInfo>
			<AnimatedSpan>
				<div className="my-1 flex items-center gap-2">
					<span className="text-muted-foreground/50 text-xs tracking-widest">
						{m.hero_navigate()}
					</span>
					<div className="flex-1 border-muted-foreground/25 border-t" />
				</div>
			</AnimatedSpan>
			<TerminalLink
				to="/works"
				label={m.hero_works()}
				description={m.hero_works_desc()}
			/>
			<TerminalLink
				to="/notes"
				label={m.hero_notes()}
				description={m.hero_notes_desc()}
			/>
			<TerminalLink
				noRef
				target={target}
				to={contactHref as LinkTo}
				label={contactLabel}
				description={contactMsg}
			/>
			<TerminalLink
				to="https://github.com/sohanemon"
				label={m.hero_github_label()}
				target="_blank"
				description={m.hero_github_desc()}
			/>
			<AnimatedSpan className="flex pt-4 text-primary">
				<span className="ms-2 me-2">~</span>
				<TypeWriter />
			</AnimatedSpan>
		</Terminal>
	);

	if (bare) return terminal;

	return (
		<div
			className={cn(
				'flex! relative flex-col items-center justify-center',
				className,
			)}
			{...props}
		>
			{terminal}
		</div>
	);
}
